import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography, useTheme } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DataObjectIcon from '@mui/icons-material/DataObject';
import PageHeader from '../../components/common/PageHeader';
import { sandboxService } from '../../services/sandboxService';
import { useNotification } from '../../hooks/NotificationContext';

/** API-Dokumentation der isolierten Umgebung (Markdown vom Backend, fuer LLMs kopierbar). */
const SandboxApiDocsPage: React.FC = () => {
  const theme = useTheme();
  const { showSuccess, showError } = useNotification();
  const [markdown, setMarkdown] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    sandboxService
      .docsMarkdown()
      .then(setMarkdown)
      .catch(() => setError('Dokumentation konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      showSuccess('Dokumentation als Markdown kopiert');
    } catch {
      showError('Kopieren nicht möglich');
    }
  };

  const isDark = theme.palette.mode === 'dark';

  return (
    <Box>
      <PageHeader
        title="API-Dokumentation (Restricted API)"
        subtitle="Nur die Endpoints der isolierten Umgebung – für AI-Tools gedacht"
        actions={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copy} disabled={!markdown}>
              Als Markdown kopieren
            </Button>
            <Button variant="outlined" startIcon={<DataObjectIcon />} href={sandboxService.openapiUrl} target="_blank" rel="noopener">
              openapi.json
            </Button>
            <Button variant="contained" startIcon={<OpenInNewIcon />} href={sandboxService.docsUrl} target="_blank" rel="noopener">
              Swagger UI
            </Button>
          </Box>
        }
      />

      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && (
        <Card>
          <CardContent
            sx={{
              '& pre': {
                bgcolor: isDark ? '#12162a' : '#f4f6f8',
                p: 1.5,
                borderRadius: 1,
                overflowX: 'auto',
                fontSize: '0.8rem',
              },
              '& code': { fontFamily: 'monospace', fontSize: '0.85em' },
              '& table': { borderCollapse: 'collapse', width: '100%', my: 1.5, fontSize: '0.85rem' },
              '& th, & td': { border: `1px solid ${theme.palette.divider}`, p: 0.75, textAlign: 'left', verticalAlign: 'top' },
              '& th': { bgcolor: isDark ? '#262b50' : '#eef1f5' },
              '& h1': { fontSize: '1.4rem', mt: 0 },
              '& h2': { fontSize: '1.1rem', mt: 3 },
              '& a': { color: theme.palette.primary.main },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Hinweis: Die Swagger UI und openapi.json erfordern eine aktive Sitzung (Cookie oder Bearer-Token).
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SandboxApiDocsPage;
